import React, { useCallback, useMemo, useState } from 'react'
import { Tooltip } from '@mui/material'
import { CircularProgress } from '@mui/joy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { CanonicalLoad } from '@numeo/types-core'
import { cn } from 'utils/cn'
import { getFormattedPhone } from '../../utils/phone'
import { buildCallHref, getCallProviderLabel, opensInNewTab } from '../../utils/callProvider'
import { useCallProvider } from '../../hooks/useCallProvider'
import { PhoneOutgoing, ShieldAlert as ShieldAlertIcon } from 'lucide-react'
import { CallIcon, EmailIcon } from '../../icons'
import { getSchneiderLoadDetail } from '../../api/loads.api'
import { getExternalLoadUrl } from '../../utils/externalUrls'
import { SylectusDetailData } from '../../hooks/useLoadDetailFetch'
import { ConvoyOfferDetail } from '../../api/convoy.api'
import { FactoringApprovalChip } from './FactoringApprovalChip'
import { BrokerStatusChip } from './BrokerStatusChip'
import { FactoringProvider } from 'services/factoring'
import { useFactoringProviderStore } from 'hooks/factoringProviderStore'

/** Inline copy-icon button used next to value text */
const CopyButton: React.FC<{ copied: boolean; onClick: () => void }> = ({ copied, onClick }) => (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} arrow placement="top">
        <button
            onClick={onClick}
            className="w-5 h-5 flex items-center justify-center border-none bg-transparent cursor-pointer text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)] transition-colors shrink-0"
        >
            {copied ? (
                <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-[var(--lh-success)]"
                >
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
            )}
        </button>
    </Tooltip>
)

/** Uber Freight rate calculator — mirrors the Miles/DH/Rate grid from TripScheduleSection */
const UberFreightRateCalc: React.FC<{ load: CanonicalLoad }> = ({ load }) => {
    const [miles, setMiles] = useState(() => (load.distanceMiles ? String(Math.round(Number(load.distanceMiles))) : ''))
    const [dh, setDh] = useState('')
    const [rateStr, setRateStr] = useState(() => (load.rateAmountUsd ? String(Math.round(Number(load.rateAmountUsd))) : ''))

    const handleInput = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/[^0-9.]/g, '')
        setter(v)
    }

    const milesNum = parseFloat(miles) || 0
    const dhNum = parseFloat(dh) || 0
    const rateNum = parseFloat(rateStr) || 0
    const totalWithDH = milesNum + dhNum
    const rpm = rateNum && milesNum > 0 ? (rateNum / milesNum).toFixed(2) : null
    const rpmWithDH = rateNum && totalWithDH > 0 ? (rateNum / totalWithDH).toFixed(2) : null

    return (
        <div className="pb-2 mb-1 border-b border-[var(--lh-border)]">
            <div className="grid grid-cols-3 gap-1 mb-1">
                <div>
                    <div className="text-[10px] text-[var(--lh-fg-muted)] mb-0.5 leading-none">Miles</div>
                    <input
                        type="text"
                        value={miles}
                        onChange={handleInput(setMiles)}
                        placeholder="-"
                        className="w-full h-6 px-1.5 text-[11px] font-semibold text-[var(--lh-fg)] rounded border border-[var(--lh-border)] bg-transparent outline-none focus:border-[var(--lh-accent)] tabular-nums"
                    />
                </div>
                <div>
                    <div className="text-[10px] text-[var(--lh-fg-muted)] mb-0.5 leading-none">DH</div>
                    <input
                        type="text"
                        value={dh}
                        onChange={handleInput(setDh)}
                        placeholder="-"
                        className="w-full h-6 px-1.5 text-[11px] font-semibold text-[var(--lh-fg)] rounded border border-[var(--lh-border)] bg-transparent outline-none focus:border-[var(--lh-accent)] tabular-nums"
                    />
                </div>
                <div>
                    <div className="text-[10px] text-[var(--lh-fg-muted)] mb-0.5 leading-none">Rate $</div>
                    <input
                        type="text"
                        value={rateStr}
                        onChange={handleInput(setRateStr)}
                        placeholder="-"
                        className="w-full h-6 px-1.5 text-[11px] font-semibold text-[var(--lh-fg)] rounded border border-[var(--lh-border)] bg-transparent outline-none focus:border-[var(--lh-accent)] tabular-nums"
                    />
                </div>
            </div>
            <div className="grid grid-cols-3 gap-1">
                <div className="rounded border border-[var(--lh-border)] px-1.5 py-1">
                    <div className="text-[10px] text-[var(--lh-fg-muted)] leading-none">Total</div>
                    <div className="text-[11px] font-semibold text-[var(--lh-fg)] tabular-nums leading-tight">
                        {totalWithDH > 0 ? Math.round(totalWithDH) : '-'}
                    </div>
                </div>
                <div className="rounded border border-[var(--lh-border)] px-1.5 py-1">
                    <div className="text-[10px] text-[var(--lh-fg-muted)] leading-none">RPM</div>
                    <div className="text-[11px] font-semibold text-[var(--lh-accent)] tabular-nums leading-tight">{rpm ? `$${rpm}` : '-'}</div>
                </div>
                <div className="rounded border border-[var(--lh-border)] px-1.5 py-1">
                    <div className="text-[10px] text-[var(--lh-fg-muted)] leading-none">RPM+DH</div>
                    <div className="text-[11px] font-semibold text-[var(--lh-accent)] tabular-nums leading-tight">{rpmWithDH ? `$${rpmWithDH}` : '-'}</div>
                </div>
            </div>
        </div>
    )
}

interface InformationSectionProps {
    load: CanonicalLoad
    hasDetailView: boolean
    loadDetailLoading: boolean
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void
    onEmailClick?: (load: CanonicalLoad) => void
    sendingEmail?: boolean
    errorEmail?: boolean
    onOpenOfferBook?: (type: 'offer' | 'book') => void
    onInteraction?: (action: 'call_clicked' | 'external_loadboard_opened', value?: string) => void
    onContactCopied?: (field: 'phone' | 'email' | 'mc' | 'dot') => void
    sylectusDetail?: SylectusDetailData | null
    /** When the row is Convoy, the parent fetches OfferPage once and passes it down so this
     * column can show the real broker name + MC (the search projection only carries "Convoy"). */
    convoyDetail?: ConvoyOfferDetail | null
}

export const InformationSection: React.FC<InformationSectionProps> = ({
    load,
    hasDetailView,
    loadDetailLoading,
    onLoadUpdate,
    onEmailClick,
    sendingEmail,
    errorEmail,
    onOpenOfferBook,
    onInteraction,
    onContactCopied,
    sylectusDetail,
    convoyDetail,
}) => {
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [schneiderContactLoading, setSchneiderContactLoading] = useState(false)
    const [schneiderContactFetched, setSchneiderContactFetched] = useState(false)

    const handleCopy = useCallback(
        async (text: string, field: 'phone' | 'email' | 'mc' | 'dot') => {
            try {
                await navigator.clipboard.writeText(text)
                setCopiedField(field)
                onContactCopied?.(field)
                setTimeout(() => setCopiedField(null), 2000)
            } catch (error) {
                console.error('Copy failed:', error)
            }
        },
        [onContactCopied]
    )

    // Numeo Loads (Broker Portal) the requesting carrier doesn't currently meet the
    // compliance rules for: the load itself is still shown (see repository.ts's
    // eligibility_matched SELECT + redactUnmatchedBrokerPortalLoad, loads-ingest-service),
    // but the backend has already nulled out contact/comment fields — this flag drives
    // showing "Verify with Numeo" in their place instead of a blank/em-dash placeholder.
    const needsVerification = load.sourceType === 'BROKER_PORTAL' && load.eligibilityMatched === false

    const isSchneider = load.sourceType === 'SCHNEIDER'

    // Outgo factorability is per-load (from details.is_factorable) — pass it to the chip so
    // it shows the real Factorable / Not-factorable status. Outgo needs no MC, so the chip
    // renders for it even when the load carries no broker MC.
    const isOutgoSelected = useFactoringProviderStore((s) => s.provider) === FactoringProvider.OUTGO
    const loadIsFactorable = (load.details as { is_factorable?: boolean | null } | null)?.is_factorable

    const handleSchneiderContact = useCallback(async () => {
        if (!load.externalId || schneiderContactLoading || schneiderContactFetched) return
        const loadId = load.externalId.split('|')[1]
        if (!loadId) return

        setSchneiderContactLoading(true)
        try {
            const detail = await getSchneiderLoadDetail(loadId)
            if (detail && onLoadUpdate) {
                onLoadUpdate({
                    ...load,
                    brokerContactPhone: detail.brokerContactPhone ?? load.brokerContactPhone,
                    brokerContactEmail: detail.brokerContactEmail ?? load.brokerContactEmail,
                })
            }
            setSchneiderContactFetched(true)
        } catch {
            // silently fail
        } finally {
            setSchneiderContactLoading(false)
        }
    }, [load, onLoadUpdate, schneiderContactLoading, schneiderContactFetched])

    const isConvoy = load.sourceType === 'CONVOY'
    // Convoy's search projection hardcodes brokerCompanyName="Convoy" and never carries an MC
    // (broker identity lives behind OfferPage). Once `convoyDetail` arrives we promote the real
    // broker name + MC into this column so the header label and factoring chip both reflect
    // who's actually brokering the load.
    const convoyBroker = convoyDetail?.shipment?.broker
    const displayBrokerName = isConvoy ? convoyBroker?.name || load.brokerCompanyName || '-' : load.brokerCompanyName || '-'
    const displayBrokerMc = isConvoy ? convoyBroker?.mcNumber || undefined : load.brokerMc

    // Name passed to the status control (avoid the "-" placeholder as a label).
    const blockBrokerName = displayBrokerName === '-' ? null : displayBrokerName

    const phoneInfo = getFormattedPhone(load.brokerContactPhone)
    const hasPhone = Boolean(phoneInfo?.formatted)
    const { provider: callProvider } = useCallProvider()
    // Falls back to a bare tel: link when the number can't be normalized for a web dialer.
    const callHref = phoneInfo?.formatted ? (buildCallHref(callProvider, phoneInfo.formatted) ?? `tel:${phoneInfo.formatted}`) : null
    const callTarget = opensInNewTab(callProvider) ? '_blank' : undefined
    const callRel = callTarget ? 'noopener noreferrer' : undefined
    const rawContactEmail = load.brokerContactEmail?.trim() || ''
    const emailList = rawContactEmail
        ? rawContactEmail
              .split(/[,;]\s*/)
              .map((e) => e.trim())
              .filter(Boolean)
        : []
    const contactEmail = rawContactEmail
    const firstEmail = emailList[0] || ''
    const hasEmail = emailList.length > 0

    const isSylectus = load.sourceType === 'SYLECTUS'
    const externalDetailUrl = useMemo(() => getExternalLoadUrl(load), [load])

    return (
        <div className="w-full h-full min-w-0 flex flex-col overflow-hidden">
            {/* Header — broker name + circular email/phone action buttons (reference line 8605-8647) */}
            <div className="flex items-center justify-between px-1 pb-2 border-b border-[var(--lh-border)] gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[14px] font-medium text-[var(--lh-fg)] truncate">{displayBrokerName}</span>
                    <BrokerStatusChip brokerMc={displayBrokerMc} brokerName={blockBrokerName} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {hasEmail && (
                        <button
                            onClick={() => {
                                if (!sendingEmail && !errorEmail) onEmailClick?.(load)
                            }}
                            disabled={sendingEmail || errorEmail}
                            title="Send email"
                            className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] border-none hover:bg-[var(--lh-accent-soft-hover)] transition-colors disabled:opacity-50"
                        >
                            {sendingEmail ? (
                                <span className="w-3.5 h-3.5 border-2 border-[var(--lh-accent)] border-t-transparent rounded-full animate-spin" />
                            ) : errorEmail ? (
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-[var(--lh-danger)]"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            ) : (
                                <EmailIcon sx={{ width: '14px', height: '14px' }} />
                            )}
                        </button>
                    )}
                    {hasPhone && callHref && (
                        <a
                            href={callHref}
                            target={callTarget}
                            rel={callRel}
                            title="Call broker"
                            onClick={() => onInteraction?.('call_clicked', callProvider)}
                            className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] hover:bg-[var(--lh-accent-soft-hover)] transition-colors no-underline"
                        >
                            <CallIcon sx={{ width: '14px', height: '14px' }} />
                        </a>
                    )}
                    {needsVerification && (
                        <button
                            type="button"
                            onClick={() => window.location.assign('/settings?verifyMc=1')}
                            title="Verify your MC with Numeo to see broker contact info"
                            className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer bg-[var(--lh-warning-soft)] text-[var(--lh-warning)] border-none hover:opacity-80 transition-opacity"
                        >
                            <ShieldAlertIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Approval section — reference "Triumph | Approved" chip (line 8649-8687).
             * For Convoy we use the MC pulled from OfferPage so the chip renders even though
             * the search projection didn't carry one. */}
            {(displayBrokerMc || isOutgoSelected) && (
                <div className="px-1 pt-2">
                    <FactoringApprovalChip mcNumber={displayBrokerMc} isFactorable={loadIsFactorable} />
                </div>
            )}

            {/* Schneider: View Contact to Book button */}
            {isSchneider && !schneiderContactFetched && !hasPhone && !hasEmail && (
                <div className="px-3 py-2 border-b border-[var(--lh-border)]">
                    <button
                        onClick={handleSchneiderContact}
                        disabled={schneiderContactLoading}
                        className="inline-flex items-center gap-2 h-8 px-4 text-[12px] font-medium text-[var(--lh-accent-foreground)] bg-[var(--lh-warning)] hover:opacity-90 disabled:opacity-60 rounded-lg transition-opacity"
                    >
                        {schneiderContactLoading ? (
                            <>
                                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Loading...
                            </>
                        ) : (
                            <>
                                <CallIcon sx={{ width: '14px', height: '14px' }} />
                                View Contact to Book
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Contact & Details — unified label/value rows, 12px, muted label + bold value */}
            {hasDetailView && loadDetailLoading ? (
                <div className="flex items-center justify-center h-full min-h-[120px]">
                    <CircularProgress size="sm" />
                </div>
            ) : (
                <div className="flex-1 px-1 pt-2 flex flex-col gap-1 overflow-y-auto min-h-0">
                    {/* Uber Freight: Miles / DH / Rate calculator (moved from col 2) */}
                    {load.sourceType === 'UBER_FREIGHT' && <UberFreightRateCalc load={load} />}
                    {/* Numeo Load the carrier doesn't currently qualify for: contact info is
                     * already redacted server-side (loads-ingest-service's
                     * redactUnmatchedBrokerPortalLoad), so there is nothing to show in the
                     * Email/Phone rows below — a single combined prompt replaces both instead
                     * of two separate "—" rows, and routes to the same MC-verification flow
                     * CompanySwitcher already links to. */}
                    {needsVerification && (
                        <div className="flex items-center justify-between gap-2 px-2 py-2 my-1 rounded-lg bg-[var(--lh-warning-soft)]">
                            <div className="flex items-center gap-1.5 min-w-0 text-[12px] text-[var(--lh-warning)]">
                                <ShieldAlertIcon className="w-3.5 h-3.5 shrink-0" />
                                <span className="font-medium truncate">Contact info hidden — MC verification required</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => window.location.assign('/settings?verifyMc=1')}
                                className="shrink-0 h-6 px-2.5 rounded-md text-[11px] font-semibold text-white bg-[var(--lh-warning)] hover:opacity-90 transition-opacity border-none cursor-pointer"
                            >
                                Verify with Numeo
                            </button>
                        </div>
                    )}
                    {/* Convoy never carries a broker email/phone (the platform doesn't expose
                     * direct broker contact — booking goes through Convoy itself), so we drop
                     * the placeholder rows entirely instead of rendering "—" twice. */}
                    {!isConvoy && !needsVerification && (
                        <div className="flex justify-between items-center py-1 text-[12px] gap-2">
                            <span className="text-[var(--lh-fg-muted)] shrink-0">Email:</span>
                            {hasEmail ? (
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span
                                        className="font-semibold text-[var(--lh-accent)] cursor-pointer hover:opacity-80 truncate max-w-[180px]"
                                        onClick={() => handleCopy(contactEmail, 'email')}
                                    >
                                        {firstEmail}
                                    </span>
                                    {emailList.length > 1 && (
                                        <Tooltip
                                            title={
                                                <div className="flex flex-col gap-0.5">
                                                    {emailList.slice(1).map((e, i) => (
                                                        <div key={i} className="text-[11px]">
                                                            {e}
                                                        </div>
                                                    ))}
                                                </div>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <span className="text-[10px] font-semibold text-[var(--lh-accent)] cursor-default">+{emailList.length - 1}</span>
                                        </Tooltip>
                                    )}
                                    <CopyButton copied={copiedField === 'email'} onClick={() => handleCopy(contactEmail, 'email')} />
                                </div>
                            ) : (
                                <span className="text-[var(--lh-fg-subtle)]">—</span>
                            )}
                        </div>
                    )}

                    {!isConvoy && !needsVerification && (
                        <div className="flex justify-between items-center py-1 text-[12px] gap-2">
                            <span className="text-[var(--lh-fg-muted)] shrink-0">Phone:</span>
                            {hasPhone ? (
                                <div className="flex items-center gap-1.5">
                                    <a
                                        href={callHref ?? `tel:${phoneInfo?.formatted || ''}`}
                                        target={callTarget}
                                        rel={callRel}
                                        title={`Call${callProvider !== 'call' ? ` via ${getCallProviderLabel(callProvider)}` : ''}`}
                                        onClick={() => onInteraction?.('call_clicked', callProvider)}
                                        className="w-4 h-4 flex items-center justify-center text-[var(--lh-accent)] hover:opacity-80 no-underline shrink-0"
                                    >
                                        <PhoneOutgoing className="w-3 h-3" />
                                    </a>
                                    <a
                                        href={callHref ?? `tel:${phoneInfo?.formatted || ''}`}
                                        target={callTarget}
                                        rel={callRel}
                                        onClick={() => onInteraction?.('call_clicked', callProvider)}
                                        className="font-semibold text-[var(--lh-accent)] tabular-nums hover:opacity-80 no-underline"
                                    >
                                        {phoneInfo?.formatted}
                                    </a>
                                    <CopyButton
                                        copied={copiedField === 'phone'}
                                        onClick={() => handleCopy(phoneInfo?.copyValue || phoneInfo?.formatted || '', 'phone')}
                                    />
                                </div>
                            ) : (
                                <span className="text-[var(--lh-fg-subtle)]">—</span>
                            )}
                        </div>
                    )}

                    {displayBrokerMc && (
                        <div className="flex justify-between items-center py-1 text-[12px] gap-2">
                            <span className="text-[var(--lh-fg-muted)] shrink-0">MC:</span>
                            <div className="flex items-center gap-1.5">
                                <span
                                    className="font-semibold text-[var(--lh-fg)] tabular-nums cursor-pointer hover:opacity-80"
                                    onClick={() => handleCopy(displayBrokerMc, 'mc')}
                                >
                                    {displayBrokerMc}
                                </span>
                                <CopyButton copied={copiedField === 'mc'} onClick={() => handleCopy(displayBrokerMc, 'mc')} />
                            </div>
                        </div>
                    )}

                    {/* Load-specific rows (Commodity, Equipment, Load type, Requirements, Comments) moved to EquipmentDetailsSection (col 1). */}

                    {/* Sylectus Load Detail */}
                    {isSylectus && sylectusDetail && (
                        <>
                            {sylectusDetail.postedAmount && sylectusDetail.postedAmount !== '$0.00 USD' && (
                                <div className="flex justify-between items-center py-1 text-[12px]">
                                    <span className="text-[var(--lh-fg-muted)]">Posted Amount:</span>
                                    <span className="font-semibold text-[var(--lh-success)]">{sylectusDetail.postedAmount}</span>
                                </div>
                            )}
                            {sylectusDetail.vehicleRequired && (
                                <div className="flex justify-between items-center py-1 text-[12px]">
                                    <span className="text-[var(--lh-fg-muted)]">Vehicle Required:</span>
                                    <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.vehicleRequired}</span>
                                </div>
                            )}
                            {sylectusDetail.dimensions && (
                                <div className="flex justify-between items-center py-1 text-[12px]">
                                    <span className="text-[var(--lh-fg-muted)]">Dimensions:</span>
                                    <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.dimensions}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center py-1 text-[12px]">
                                <span className="text-[var(--lh-fg-muted)]">Dock Level:</span>
                                <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.dockLevel || '—'}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 text-[12px]">
                                <span className="text-[var(--lh-fg-muted)]">Hazmat:</span>
                                <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.hazmat || '—'}</span>
                            </div>
                            <div className="flex justify-between items-center py-1 text-[12px]">
                                <span className="text-[var(--lh-fg-muted)]">Stackable:</span>
                                <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.stackable || '—'}</span>
                            </div>
                            {sylectusDetail.csaFastLoad && sylectusDetail.csaFastLoad !== 'No' && (
                                <div className="flex justify-between items-center py-1 text-[12px]">
                                    <span className="text-[var(--lh-fg-muted)]">CSA/Fast Load:</span>
                                    <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.csaFastLoad}</span>
                                </div>
                            )}
                            {sylectusDetail.stopsInfo && (
                                <div className="flex justify-between items-center py-1 text-[12px]">
                                    <span className="text-[var(--lh-fg-muted)]">Stops:</span>
                                    <span className="font-semibold text-[var(--lh-fg)]">{sylectusDetail.stopsInfo}</span>
                                </div>
                            )}
                            {sylectusDetail.notes && (
                                <div className="py-1 text-[12px]">
                                    <span className="text-[var(--lh-fg-muted)]">Notes:</span>
                                    <p className="font-semibold text-[var(--lh-fg)] mt-0.5 whitespace-pre-line">{sylectusDetail.notes}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Bottom action buttons — Book / Offer (reference line 8727-8765). Both same style: accent-soft bg + accent text. */}
            {hasEmail && onOpenOfferBook && (
                <div className="flex gap-2 px-1 pt-2 mt-auto shrink-0">
                    <button
                        onClick={() => onOpenOfferBook('book')}
                        className="flex-1 h-7 text-[12px] font-medium bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] hover:bg-[var(--lh-accent-soft-hover)] rounded-lg border-none transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                        <EmailIcon sx={{ width: '14px', height: '14px' }} />
                        Book
                    </button>
                    <button
                        onClick={() => onOpenOfferBook('offer')}
                        className="flex-1 h-7 text-[12px] font-medium bg-[var(--lh-accent-soft)] text-[var(--lh-accent)] hover:bg-[var(--lh-accent-soft-hover)] rounded-lg border-none transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                        <EmailIcon sx={{ width: '14px', height: '14px' }} />
                        Offer
                    </button>
                </div>
            )}

            {/* Full-width Load Details link — always rendered at the very bottom, after Book/Offer buttons.
             * If no email thread / buttons, this still sticks to the bottom via `mt-auto` fallback. */}
            {externalDetailUrl && (
                <a
                    href={externalDetailUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onInteraction?.('external_loadboard_opened')}
                    className={cn(
                        'w-full h-7 px-1 mx-1 flex items-center justify-center gap-1.5 rounded-lg bg-[var(--lh-accent-soft)] hover:bg-[var(--lh-accent-soft-hover)] text-[var(--lh-accent)] text-[12px] font-medium transition-colors no-underline shrink-0',
                        hasEmail && onOpenOfferBook ? 'mt-2' : 'mt-auto mt-2'
                    )}
                >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                    Load Details
                </a>
            )}
        </div>
    )
}
